import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import {
  EmptyRewardCard,
  isPlayerPending,
} from "@/components/organisms/SBCs/RewardPlayerCard";
import { TradeableChip } from "@/components/organisms/SBCs/TradeableChip";
import { Foldout } from "@/design-system/Foldout/Foldout";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import { Popover } from "@/design-system/Popover/Popover";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import { tierSelector } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { objectiveReward } from "@repo/shared/utils";
import { Info, Minus, Plus } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

// Cards shown per list before Show More.
const SHOWN = 5;

// Summed into one card each; every other type is kept whole.
const AMOUNT_TYPES = ["coins", "xp", "champions_qualification_points"];
const ORDER = [
  "player",
  "evo",
  "coins",
  "xp",
  "champions_qualification_points",
  "pack",
  "playerpick",
];

/**
 * A reward and where it is earned: the group, plus the challenge for a
 * challenge's reward. `partial` when this plan doesn't earn it.
 */
export type BundleReward = SBCRewardType & {
  source: { group: string; challenge?: string };
  partial: boolean;
};

type RawReward = BundleReward & {
  amount?: number;
  imgId?: number;
  id?: number;
};

/**
 * One entry per distinct reward: amounts summed, identical packs and items
 * counted, players and evos kept apart. Sorted players (best meta rating
 * first), evos, coins, SP, Champions points, packs, player picks, then
 * everything else.
 */
const mergeRewards = (rewards: BundleReward[]) => {
  const merged = new Map<string, { reward: RawReward; count: number }>();
  rewards.forEach((raw, i) => {
    const reward = raw as RawReward;
    const key = AMOUNT_TYPES.includes(reward.type)
      ? reward.type
      : reward.type === "player" || reward.type === ("evo" as string)
        ? `${reward.type}-${i}`
        : `${reward.type}-${reward.resourceId ?? reward.name ?? reward.imgId}`;
    const entry = merged.get(key);
    if (!entry) {
      merged.set(key, { reward: { ...reward }, count: 1 });
      return;
    }
    // A sum is partial when any part of it is.
    entry.reward.partial ||= reward.partial;
    if (AMOUNT_TYPES.includes(reward.type))
      entry.reward.amount = (entry.reward.amount ?? 0) + (reward.amount ?? 0);
    else entry.count += 1;
  });
  const rank = (type: string) =>
    ORDER.includes(type) ? ORDER.indexOf(type) : ORDER.length;
  return [...merged.values()].sort(
    (a, b) =>
      rank(a.reward.type) - rank(b.reward.type) ||
      (b.reward.metaRating?.metarating ?? 0) -
        (a.reward.metaRating?.metarating ?? 0),
  );
};

const RewardCard = ({
  reward,
  count,
  source,
  partial,
}: {
  reward: SBCRewardType;
  count: number;
  source: BundleReward["source"];
  partial: boolean;
}) => {
  const { t } = useTranslation("objectives");
  const tier = useSelector(
    tierSelector(
      reward.metaRating?.metarating || 0,
      reward.metaRating?.playerRoleId ?? null,
      true,
    ),
  );
  const isPlayer = reward.type === "player" && !!reward.player;
  const name = reward.name || reward.shortName;
  // Clicked open, the card grows to fit the whole name; only a name that is
  // cut off makes the card clickable.
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const nameRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el || expanded) return;
    const check = () => setTruncated(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded]);
  const canExpand = expanded || truncated;
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-x-2 rounded-lg border border-neutral-750 bg-neutral-800 px-3 py-2",
        !expanded && "max-w-[280px]",
      )}
    >
      <button
        type="button"
        title={name}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={canExpand ? () => setExpanded(!expanded) : undefined}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-x-3 text-left",
          canExpand ? "cursor-pointer" : "cursor-default",
        )}
      >
        {isPlayer ? (
          <div className="shrink-0">
            {isPlayerPending(reward.player) ? (
              <EmptyRewardCard scale={0.4} />
            ) : (
              <PlayerCard
                player={reward.player!}
                position={reward.player!.preferredPosition}
                scale={0.4}
                hidePrice
              />
            )}
          </div>
        ) : (
          <img
            loading="lazy"
            className="w-12 shrink-0"
            alt={name}
            src={extractRewardUrl(reward)}
          />
        )}
        <div className="flex min-w-0 flex-col gap-y-1">
          {isPlayer ? (
            <>
              <span
                ref={nameRef}
                className={cn(
                  "text-sm font-semibold text-foreground",
                  !expanded && "truncate",
                )}
              >
                {reward.player?.name}
              </span>
              {(reward.metaRating || partial) && (
                <div className="flex items-center gap-x-2">
                  {reward.metaRating && (
                    <MetaRatingChip
                      metaRating={reward.metaRating.metarating}
                      tier={tier ?? undefined}
                    />
                  )}
                  {partial && (
                    <span className="flex h-5 items-center rounded-full bg-amber-900/50 px-2 text-xs text-amber-400">
                      {t("plan.stacked.partial")}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <span
                ref={nameRef}
                className={cn(
                  "text-sm font-semibold text-foreground",
                  !expanded && "truncate",
                )}
              >
                {name}
              </span>
              {(reward.type === "pack" || partial) && (
                <div className="flex items-center gap-x-2">
                  {reward.type === "pack" && (
                    <TradeableChip
                      rewardType={reward.type}
                      untradeable={reward.untradeable}
                    />
                  )}
                  {partial && (
                    <span className="flex h-5 items-center rounded-full bg-amber-900/50 px-2 text-xs text-amber-400">
                      {t("plan.stacked.partial")}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {count > 1 && (
          <span className="ml-auto shrink-0 pl-1 text-sm font-bold text-foreground">
            {count}×
          </span>
        )}
      </button>
      {/* An evo's details aren't known until EA lists it; say where to find it in-game. */}
      {reward.type === ("evo" as string) && (
        <Popover
          title={t("plan.rewardSource")}
          negLabel=""
          posLabel=""
          trigger={
            <button
              type="button"
              aria-label={t("plan.rewardSource")}
              className="flex shrink-0 text-foreground-muted hover:text-foreground"
            >
              <Info className="size-4" />
            </button>
          }
        >
          <p className="text-sm text-foreground">{source.group}</p>
          {source.challenge && (
            <p className="text-sm text-foreground-muted">{source.challenge}</p>
          )}
        </Popover>
      )}
    </div>
  );
};

/** The bundle's rewards as small cards under a gold label. */
export const BundleRewards = ({
  label,
  rewards,
  open,
  onToggle,
}: {
  label: string;
  rewards: BundleReward[];
  open: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation(["objectives", "common"]);
  const { t: tObjectives } = useTranslation("objectives");
  const [showAll, setShowAll] = useState(false);
  const list = mergeRewards(rewards);
  if (list.length === 0) return null;
  return (
    <Foldout
      label={
        <>
          {label}
          {/* The cards shown: merged ones count once. */}
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-neutral-800 px-1.5 text-xs font-semibold text-foreground">
            {list.length}
          </span>
        </>
      }
      open={open}
      onToggle={onToggle}
    >
      <div className="flex flex-wrap gap-2">
        {(showAll ? list : list.slice(0, SHOWN)).map(({ reward, count }, i) => {
          const shown = objectiveReward(reward, tObjectives);
          return shown ? (
            <RewardCard
              key={i}
              reward={shown}
              count={count}
              source={reward.source}
              partial={reward.partial}
            />
          ) : null;
        })}
        {/* The last card of the row, in the reward cards' shell. */}
        {list.length > SHOWN && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-x-1.5 self-stretch rounded-lg border border-neutral-750 bg-neutral-800 px-3 py-2 text-sm font-semibold text-foreground-muted transition-colors hover:text-foreground"
          >
            {showAll ? (
              <Minus className="size-4" />
            ) : (
              <Plus className="size-4" />
            )}
            {showAll
              ? t("common:showLess")
              : t("plan.showMoreRewards", { count: list.length - SHOWN })}
          </button>
        )}
      </div>
    </Foldout>
  );
};
